import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PrimaryButton } from '@/components/ui-kit/Buttons'
import { MascotBubble, MascotSlot } from '@/components/ui-kit/Mascot'
import { Headline } from '@/components/ui-kit/Primitives'
import { ScreenShell, TopBar } from '@/components/ui-kit/Shell'
import { KNOWLEDGE_MAX, KNOWLEDGE_MIN } from '@/lib/game'
import { cn } from '@/lib/utils'
import type { BetExperience } from '@/types/game'

const EXPERIENCES: BetExperience[] = ['nie', 'manchmal', 'oft']

/** Die beiden Einstiegsfragen. */
export function SurveyScreen({
    onSubmit,
}: {
    onSubmit: (experience: BetExperience, knowledge: number) => void
}) {
    const { t } = useTranslation()
    const [experience, setExperience] = useState<BetExperience | null>(null)
    // WETTBANDIT-66: Die Skala war auf 5 vorbelegt und wurde deshalb oft
    // uebersehen. Ohne eigene Eingabe bleibt sie leer, und es geht nicht weiter.
    const [knowledge, setKnowledge] = useState<number | null>(null)

    const labels = t('survey.knowledge.labels', { returnObjects: true }) as string[]

    return (
        <ScreenShell>
            <TopBar title={t('survey.title')} />

            {/* Innenabstand fuer den Ring und den Schatten der Sprechblase: overflow-y-auto
                schneidet alles ab, was ueber die Box hinausragt - seit die Blase bis an
                den Rand reicht (WETTBANDIT-44), traf es rechts den Ring und unten den
                Schatten. -mx-1 haelt die Inhalte trotzdem auf ihrer Linie. */}
            <div className="-mx-1 wb-scroll flex flex-1 flex-col gap-3.5 overflow-y-auto px-1 pt-3 pb-8">
                <div className="flex items-center gap-2.5">
                    <MascotSlot pose="teaching" size={88} />
                    <MascotBubble>{t('survey.bubble')}</MascotBubble>
                </div>

                <section className="flex flex-col gap-2.5 rounded-2xl border border-wb-border bg-wb-panel p-4">
                    <span className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('survey.questionOf', { current: 1, total: 2 })}
                    </span>
                    <Headline as="h2" className="text-lg">
                        {t('survey.experience.question')}
                    </Headline>

                    <div className="mt-0.5 flex flex-col gap-1.5" role="radiogroup">
                        {EXPERIENCES.map((option) => {
                            const selected = experience === option

                            return (
                                <button
                                    key={option}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    onClick={() => setExperience(option)}
                                    className={cn(
                                        'flex items-center gap-2.5 rounded-xl border-[1.5px] px-3 py-2.5 text-left',
                                        selected
                                            ? 'border-wb-accent bg-wb-accent text-[#0a0d14]'
                                            : 'border-wb-border bg-wb-panel-hi text-wb-text',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'flex size-4.5 flex-none items-center justify-center rounded-full border-2',
                                            selected
                                                ? 'border-[#0a0d14] bg-[#0a0d14]'
                                                : 'border-wb-muted',
                                        )}
                                    >
                                        {selected && (
                                            <span className="size-1.5 rounded-full bg-wb-accent" />
                                        )}
                                    </span>
                                    <span className="text-sm leading-tight font-bold">
                                        {t(`survey.experience.${option}`)}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                </section>

                <section className="flex flex-col gap-3 rounded-2xl border border-wb-border bg-wb-panel p-4">
                    <span className="text-[10px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('survey.questionOf', { current: 2, total: 2 })}
                    </span>
                    <Headline as="h2" className="text-lg">
                        {t('survey.knowledge.question')}
                    </Headline>

                    <div className="flex items-baseline justify-between">
                        <span
                            className={cn(
                                'wb-num text-4xl leading-none font-extrabold',
                                knowledge === null ? 'text-wb-muted' : 'text-wb-accent',
                            )}
                        >
                            {knowledge ?? '–'}
                            <span className="text-base text-wb-muted"> / {KNOWLEDGE_MAX}</span>
                        </span>
                        <span
                            className="max-w-[150px] text-right text-sm font-bold text-balance"
                            data-testid="knowledge-label"
                        >
                            {knowledge === null ? t('survey.knowledge.empty') : labels[knowledge]}
                        </span>
                    </div>

                    <input
                        type="range"
                        min={KNOWLEDGE_MIN}
                        max={KNOWLEDGE_MAX}
                        step={1}
                        value={knowledge ?? Math.round((KNOWLEDGE_MIN + KNOWLEDGE_MAX) / 2)}
                        onChange={(event) => setKnowledge(Number(event.target.value))}
                        // Ohne Antwort steht der Regler optisch in der Mitte. Wer
                        // genau diesen Wert antippt, aendert den Wert nicht - ohne
                        // diesen Griff bliebe die Antwort leer und "Weiter" gesperrt.
                        onPointerUp={(event) => {
                            const value = Number(event.currentTarget.value)

                            setKnowledge((current) => current ?? value)
                        }}
                        aria-label={t('survey.knowledge.question')}
                        className={cn(
                            'w-full',
                            knowledge === null ? 'accent-wb-muted opacity-60' : 'accent-wb-accent',
                        )}
                    />

                    <div className="flex justify-between text-[11px] text-wb-muted">
                        <span>{t('survey.knowledge.min')}</span>
                        <span>{t('survey.knowledge.max')}</span>
                    </div>
                </section>
            </div>

            <div className="mt-3 flex flex-col gap-2">
                <PrimaryButton
                    disabled={experience === null || knowledge === null}
                    onClick={() =>
                        experience !== null && knowledge !== null && onSubmit(experience, knowledge)
                    }
                >
                    {t('survey.submit')}
                </PrimaryButton>
                <p className="text-center text-[11px] text-wb-muted">{t('survey.privacy')}</p>
            </div>
        </ScreenShell>
    )
}
